import { useEffect, useState } from 'react';

import { usePopper } from 'react-popper';
import { connect } from 'react-redux';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Typography } from 'src/app/components/Elements/Typography';
import { FocusLockAndEscape } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper/FocusLockAndEscape';
import { segmentTrack } from 'src/app/state/actions';
import type { Dispatch } from 'src/app/state/store';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import {
  CloseIconButton,
  AnchorContainer,
  Arrow,
  CloseIcon,
  InfoIcon,
  Overlay,
  ToolTipAnchorCopyContainer,
  ToolTipContainer,
  ToolTipContent,
  ToolTipCopyContainer,
} from './styles';

export interface ToolTipProps {
  className?: string;
  placement?: 'top-start' | 'top-end' | 'top' | 'bottom' | 'bottom-start' | 'bottom-end';
  header?: string;
  body?: string;
  anchorText?: string;
  textSize?: 'body' | 'small';
  /**
   * Allows tooltip to be controlled by redux state
   */
  isToolTipOpen?: boolean;
  onClick?: () => void;
  analyticTip?: string;
  altIconString?: string;
  blueFill?: boolean;
}

const MOBILE_MARGIN_THRESHOLD = 100;
const HALF_ARROW_WIDTH = 10;

const ToolTipComponent = ({
  className,
  isToolTipOpen,
  header,
  body,
  anchorText,
  textSize = 'body',
  placement = 'top-start',
  track,
  analyticTip,
  onClick,
  altIconString,
  blueFill = true,
}: ToolTipProps) => {
  const [isOpen, setIsOpen] = useState(isToolTipOpen);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const pathName = window.location.pathname;

  useEffect(() => {
    setIsOpen(isToolTipOpen);
  }, [isToolTipOpen]);

  // gives some space between tooltip and anchorText and adjusts alignment
  const TOOLTIP_OFFSET_Y = 50;
  let TOOLTIP_OFFSET_X = -30;

  const innerElemDistanceX = referenceElement?.getBoundingClientRect()?.x;
  if (innerElemDistanceX < MOBILE_MARGIN_THRESHOLD) {
    //divide anchor distance by 2 to center tooltip and negate value to move left
    TOOLTIP_OFFSET_X = (innerElemDistanceX / 2) * -1 - HALF_ARROW_WIDTH;
  }

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [TOOLTIP_OFFSET_X, TOOLTIP_OFFSET_Y],
        },
      },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });

  const toggleTooltip = () => {
    if (!isOpen) {
      track(UNIFIED.viewTooltip, { pagepath: pathName, tip: analyticTip });
    }
    setIsOpen(!isOpen);
    onClick?.();
  };
  const closeToolTip = () => setIsOpen(false);

  const iconString = altIconString ? altIconString : CAPSULE_ICON_STRINGS.system_info;

  return (
    <ToolTipContainer className={className}>
      <AnchorContainer onClick={toggleTooltip} type="button">
        <span ref={setReferenceElement} />
        <InfoIcon isOpen={isOpen} blueFill={blueFill} iconString={iconString} />
        <ToolTipAnchorCopyContainer>
          <Typography variant={textSize}>{anchorText}</Typography>
        </ToolTipAnchorCopyContainer>
      </AnchorContainer>
      {isOpen && (
        <>
          <Overlay onClick={closeToolTip} />
          <FocusLockAndEscape handleEscapePressed={closeToolTip}>
            <ToolTipContent ref={setPopperElement} style={styles.popper} {...attributes.popper}>
              <CloseIconButton onClick={closeToolTip} aria-label="Close Tooltip">
                <CloseIcon iconString={CAPSULE_ICON_STRINGS.close} block />
              </CloseIconButton>

              <ToolTipCopyContainer>
                <Typography variant="h2" marginBottom={'s3'}>
                  {header}
                </Typography>
                <Typography variant="body">{body}</Typography>
              </ToolTipCopyContainer>
              <Arrow ref={setArrowElement} style={styles.arrow} id="arrow" />
            </ToolTipContent>
          </FocusLockAndEscape>
        </>
      )}
    </ToolTipContainer>
  );
};

const ConnectedToolTip = connect(null, (dispatch: Dispatch) => ({
  track: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
}))(ToolTipComponent);

export { ConnectedToolTip as ToolTip, ToolTipComponent };
